/**
 * "Start with your logo": the first logo the kit receives seeds its colours, as the first design does for
 * "Start with your design". The backend reads nothing off an image, so the logo's dominant colours are pulled in the browser — the same pass the
 * editor runs on image items — and saved one at a time through the regular add-colour endpoint, most
 * dominant first. Fonts are never read off a logo; the handoff's result screen leaves them empty.
 */

import {BrandAssetType, BrandColorType, BrandKitExtractionSource} from '@Components/mystuff-brands/brand-kits.types';
import type {BrandLogo} from '@Components/mystuff-brands/brand-kits.types';
import {addBrandColor, fetchBrandAssets} from '@Components/mystuff-brands/brands-thunk';
import {finishBrandKitExtraction, showBrandKitExtractionFeedbackToast, startBrandKitExtraction} from '@Libraries/brand-kit-extraction-feedback.library';
import {getBrandLogoImageUrl, getBrandsStateFromStore, getFirstBrandKitTemplate} from '@Libraries/brands-library';
import {rgbaToHex} from '@Utils/color.util';
import {extractDominantColorsFromImage} from '@Utils/dominant-color.util';
import {loadImageAsync} from '@Utils/image.util';
import {executeThunk} from '@Utils/thunk.util';

/** The handoff seeds five swatches from the logo. */
const LOGO_SEED_COLOR_COUNT = 5;

/** My Uploads reports every uploaded file separately, and only the first report may seed the kit. */
const brandIdsBeingSeeded = new Set<string>();

/**
 * Runs once the logo picker has linked its pick into the kit it was opened for. The page
 * shows its skeleton while the colours land, then asks whether they look right.
 */
export const seedBrandKitFromPickedLogo = async (brandId: string): Promise<void> => {
  if (brandIdsBeingSeeded.has(brandId) || !isKitWaitingForSeedColors(brandId)) {
    return;
  }

  brandIdsBeingSeeded.add(brandId);
  startBrandKitExtraction();

  try {
    const hasSeededColors = await seedBrandKitColorsFromLogo(brandId);

    if (hasSeededColors) {
      showBrandKitExtractionFeedbackToast(BrandKitExtractionSource.LOGO);
    }
  } finally {
    brandIdsBeingSeeded.delete(brandId);
    finishBrandKitExtraction();
  }
};

/** Only a kit that has a logo and no colours yet is seeded; anything the user already chose stays. */
const isKitWaitingForSeedColors = (brandId: string): boolean => {
  const content = getBrandsStateFromStore().brandContent[brandId];

  return content !== undefined && content.logos.ids.length > 0 && content.colors.ids.length === 0;
};

/** Resolves to whether any colour landed, so the caller can tell a seeded kit from an untouched one. */
const seedBrandKitColorsFromLogo = async (brandId: string): Promise<boolean> => {
  return addSeedColors(brandId, await getDominantHexCodesOfFirstLogo(brandId));
};

const addSeedColors = async (brandId: string, hexCodes: string[]): Promise<boolean> => {
  if (hexCodes.length === 0) {
    return false;
  }

  await addColorsInDominanceOrder(brandId, hexCodes);
  await fetchBrandAssets(brandId, BrandAssetType.BRAND_COLORS);
  return true;
};

const getFirstLogo = (brandId: string): BrandLogo | null => {
  const {brandContent, logos} = getBrandsStateFromStore();
  const [firstLogoId] = brandContent[brandId].logos.ids;

  return firstLogoId === undefined ? null : logos[firstLogoId];
};

/** A logo that cannot be loaded or sampled seeds nothing; the kit simply stays without colours. */
const getDominantHexCodesOfFirstLogo = async (brandId: string): Promise<string[]> => {
  const logo = getFirstLogo(brandId);

  if (logo === null) {
    return [];
  }

  return getDominantHexCodesOfImage(getBrandLogoImageUrl(logo));
};

/** An image that cannot be loaded or sampled gives no colours. */
const getDominantHexCodesOfImage = async (imageUrl: string): Promise<string[]> => {
  try {
    const image = await loadImageAsync(imageUrl);

    return extractDominantColorsFromImage(image)
      .slice(0, LOGO_SEED_COLOR_COUNT)
      .map((color) => {
        return rgbaToHex(color, false);
      });
  } catch {
    return [];
  }
};

/**
 * "Start with your design": its colours are read in the browser off the first template's preview, by the same
 * pass a logo gets (its fonts are read on the server, off the design's text). Resolves to whether any landed.
 */
export const seedBrandKitColorsFromFirstTemplate = async (brandId: string): Promise<boolean> => {
  const {brandContent} = getBrandsStateFromStore();
  const previewUrl = getFirstBrandKitTemplate(brandId)?.previewUrl;

  if (!previewUrl || brandContent[brandId].colors.ids.length > 0) {
    return false;
  }

  return addSeedColors(brandId, await getDominantHexCodesOfImage(previewUrl));
};

/** One request at a time: the kit lists colours in insertion order, so the most dominant must land first. */
const addColorsInDominanceOrder = async (brandId: string, hexCodes: string[]): Promise<void> => {
  await hexCodes.reduce(async (previousAdds: Promise<void>, hexCode: string): Promise<void> => {
    await previousAdds;
    await executeThunk(() => {
      return window.PMW.redux.store.dispatch(addBrandColor({brandId, hexCode, type: BrandColorType.OTHER}));
    });
  }, Promise.resolve());
};
